import React, { useEffect, useRef } from 'react'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import useRect from '@jimmyboys/liita-ui/dist/hooks/useRect'
import useCalculateTextsHeights from '@jimmyboys/liita-ui/dist/hooks/useCalculateTextsHeights'
import Collection from '@jimmyboys/liita-ui/dist/components/Collection'

import { useParams } from 'react-router-dom'

import { useFetchList } from '../../store'

import {
	ClinicalEvaluation,
	FinalSituation,
	Comment,
	ClinicalEpisode,
	ProcessForwarding,
	ClinicalProcess,
} from 'models'

import ClinicalEvaluationCard, {
	ClinicalEvaluationCardSkeleton,
} from 'components/cards/ClinicalEvaluationCard/ClinicalEvaluationCard'

import FinalSituationCard from 'components/cards/FinalSituationCard/FinalSituationCard'
import EpisodesSeparatorCard from 'components/cards/EpisodesSeparatorCard/EpisodesSeparatorCard'

import NoData from 'components/general/NoData/NoData'
import { calculateItemHeight, LINE_SPLIT_REGEX } from './utils'

import themeStyles from 'assets/styles/theme.module.sass'
import useCollectionSpecs, {
	getThemeSpacing,
	getThemeVariable,
} from 'hooks/useCollectionSpecs'

import styles from './ClinicalProcess.module.sass'
import { currentDetailLevelState } from 'store/atoms'
import { useRecoilValue } from 'recoil'
import MedicalOpinion from 'models/MedicalOpinion'
import MedicalOpinionCard from 'components/cards/MedicalOpinionCard/MedicalOpinionCard'
import MedicalOpinionTriageCard from 'components/cards/MedicalOpinionTriageCard/MedicalOpinionTriageCard'
import ProcessForwardingCard from 'components/cards/ProcessForwardingCard/ProcessForwardingCard'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import { NotificationType } from '../../utils/enums'
import { fetchOptions } from '../../store/fetchUtils'

const { phi, smallFontSize } = themeStyles
const [themeVerticalSpacing, themeHorizontalSpacing] = getThemeSpacing()
const themeSmallFontSize = getThemeVariable(smallFontSize)

const ClinicalOpinionsController = () => {
	const { i18n, screenSize } = useGlobal()
	const { dispatch } = useDrawerContext()
	const { clinicalProcessId } = useParams()

	const collectionRef = useRef(null)
	const rect = useRect(collectionRef)
	const detailLevel = useRecoilValue(currentDetailLevelState)

	const clinicalEvaluationsUri = `/clinical_processes/${clinicalProcessId}/clinical_evaluations`
	const finalSituationUri = `/clinical_processes/${clinicalProcessId}/final_situations`
	const commentsUri = `/clinical_processes/${clinicalProcessId}/comments`
	const clinicalEpisodesUri = `/clinical_processes/${clinicalProcessId}/clinical_episodes`
	const opinionsUri = `/clinical_processes/${clinicalProcessId}/medical_opinion`
	const processForwardUri = `/clinical_processes/${clinicalProcessId}/process_forward`
	const processUri = `/clinical_processes/${clinicalProcessId}`
	const notificationsUri = `/clinical_processes/${clinicalProcessId}/all_notifications`

	const cardLabels = {
		doctor: i18n`Doctor`,
		date: i18n`Date`,
		causalLink: i18n`Causal Link`,
		clinicalEvaluation: i18n`Clinical Evaluation`,
		disabilityPrediction: i18n`Disability prediction`,
		medicalObservation: i18n`Medical Observation`,
		disability: i18n`Disability`,
		yes: i18n`Yes`,
		no: i18n`No`,
		dubious: i18n`Dubious`,
		remainsTheSame: i18n`Remains the same`,
		notDefined: i18n`Not defined`,
		empty: i18n`Empty`,

		PTDOW: i18n`PTDOW`,
		additionalEfforts: i18n`Additional efforts`,
		PPD: i18n`PPD`,
		biologicalDamage: i18n`Biological damage`,
		treatmentDays: i18n`Treatment days`,
		temp: i18n`Temp`,
		TTD: i18n`TTD`,
		weight: i18n`Weight`,

		evaluation: i18n`Evaluation`,
		prediction: i18n`Prediction`,
		ippForecast: i18n`IPP Forecast`,

		medicalOpinion: i18n`Clinical opinion`,
		triage: i18n`Triage`,
		causalDoubts: i18n`Causal Doubts`,
		processType: i18n`Process Type`,

		forwarding: i18n`Forwarding task`,
	}

	const cardLayout = 'vertical'

	const cards = {
		ClinicalEvaluationCardSkeleton,
		ClinicalEvaluationCard,
		FinalSituationCard,
		EpisodesSeparatorCard,
		MedicalOpinionCard,
		MedicalOpinionTriageCard,
		ProcessForwardingCard,
	}

	const { state, contents } = useFetchList(
		[
			clinicalEvaluationsUri,
			finalSituationUri,
			commentsUri,
			clinicalEpisodesUri,
			opinionsUri,
			processForwardUri,
			processUri,
			notificationsUri,
		],
		[
			ClinicalEvaluation.fromJSON,
			FinalSituation.fromJSON,
			Comment.fromJSON,
			ClinicalEpisode.fromJSON,
			MedicalOpinion.fromJSON,
			ProcessForwarding.fromJSON,
			ClinicalProcess.fromJSON,
			(json) => json,
		]
	)
	const isLoading = state === 'loading'
	const prevClinicalStatusRef = useRef(null)

	const [
		clinicalEvaluationsData,
		finalSituationsData,
		commentsData,
		clinicalEpisodesData,
		opinionsData,
		processForwardData,
		clinicalProcess,
		notificationsData,
	] = contents

	useEffect(() => {
		const currentStatus = clinicalProcess?.clinicalStatus?.value
		const prevStatus = prevClinicalStatusRef.current

		if (currentStatus === 'EXECUTED' && prevStatus !== 'EXECUTED') {
			const acutePhaseNotification = (notificationsData || []).find(
				(n) =>
					n.notificationTypeId === NotificationType.AcutePhase &&
					!n.isRead
			)

			if (acutePhaseNotification) {
				fetch(
					`/api/notifications/${acutePhaseNotification.id}`,
					fetchOptions({
						method: 'PUT',
						body: { isRead: true, closed: true },
					})
				)
			}
		}

		prevClinicalStatusRef.current = currentStatus
	}, [clinicalProcess, notificationsData])

	const clinicalEvaluations = (clinicalEvaluationsData || []).map((item) => {
		const doctorId = item.doctor?.id
		const timestamp = item.date?.timestamp
		const clinicalEvaluationComment = doctorId
			? (commentsData || []).find(({ author, date }) => {
					const deltaTime = date?.timestamp
						? Math.abs(date?.timestamp - timestamp)
						: 10000
					return (
						author?.id && author.id === doctorId && deltaTime < 1000
					)
			  })
			: null
		return {
			...item,
			body: item.body || clinicalEvaluationComment?.body,
			text: item.body || clinicalEvaluationComment?.text,
			sortParam: item.date?.timestamp,
			$cardName: 'ClinicalEvaluationCard',
			$cardLabels: cardLabels,
			$layout: cardLayout,
			$detailLevel: detailLevel,
		}
	})
	const finalSituations = (finalSituationsData || []).map((item) => {
		return {
			...item,
			sortParam: item.date?.timestamp,
			$cardName: 'FinalSituationCard',
			$cardLabels: cardLabels,
			$layout: cardLayout,
			$detailLevel: detailLevel,
		}
	})

	const processForward = processForwardData
		? {
				...processForwardData,
				sortParam: processForwardData.completionDate?.timestamp,
				$cardName: 'ProcessForwardingCard',
				$cardLabels: cardLabels,
				$layout: cardLayout,
				$detailLevel: detailLevel,
				$action: () => {
					dispatch({
						action: 'OPEN_DRAWER',
						payload: {
							type: 'ForwardingDrawer',
							data: processForwardData,
							process: clinicalProcess,
						},
					})
				},
		  }
		: null

	const clinicalEpisodes =
		(clinicalEvaluations.length || finalSituations.length) &&
		clinicalEpisodesData
			? clinicalEpisodesData.slice(1).map((json, index) => {
					return {
						...json,
						prevEpisode: clinicalEpisodesData[index],
						sortParam: json.date.timestamp - 1001,
						$cardName: 'EpisodesSeparatorCard',
						$layout: cardLayout,
					}
			  })
			: []

	const medicalOpinions = (opinionsData || []).map((json) => {
		let cardName = ''
		switch (json.medicalObservationType) {
			case 'Triagem':
				cardName = 'MedicalOpinionTriageCard'
				break
			default:
				cardName = 'MedicalOpinionCard'
		}

		const acutePhaseNotification = (notificationsData || []).find(
			(n) => n.notificationTypeId === NotificationType.AcutePhase
		)
		return {
			...json,
			acutePhaseNotificationDate: acutePhaseNotification?.postponedDate,
			sortParam:
				json.responseDate.timestamp || json.creationDate.timestamp,
			$cardName: cardName,
			$detailLevel: detailLevel,
		}
	})

	let data = [
		...clinicalEvaluations,
		...finalSituations,
		...clinicalEpisodes,
		...medicalOpinions,
	].sort((a, b) => b.sortParam - a.sortParam)

	if (processForward) {
		data.push(processForward)
	}

	data.sort((a, b) => b.sortParam - a.sortParam)

	const horizontalPadding = (themeHorizontalSpacing / phi) * 2
	const horizontalMargin =
		screenSize === 'small'
			? (themeHorizontalSpacing / phi) * 2 +
			  (themeHorizontalSpacing / phi) * 5
			: (themeHorizontalSpacing / phi) * 6 +
			  (themeHorizontalSpacing / phi) * 3

	const textSpecs = useCollectionSpecs(
		collectionRef,
		rect,
		horizontalPadding + horizontalMargin,
		themeSmallFontSize
	)

	const cardMargin = themeVerticalSpacing / phi

	const isBeforeTextComputation = !(textSpecs.fontSize && textSpecs.maxWidth)

	const textsHeights = useCalculateTextsHeights(
		isBeforeTextComputation ? [] : data,
		textSpecs.fontSize
			? `${textSpecs.fontWeight} ${textSpecs.fontSize}px ${
					textSpecs.fontFamily || ''
			  }`
			: null,
		textSpecs.letterSpacing,
		textSpecs.maxWidth,
		LINE_SPLIT_REGEX,
		screenSize
	)

	let collectionItems
	let itemsHeights
	if (isLoading) {
		const loadingItems = new Array(5).fill({})
		collectionItems = loadingItems.map(() => {
			return {
				$cardName: 'ClinicalEvaluationCardSkeleton',
				$layout: cardLayout,
			}
		})
		itemsHeights = loadingItems.map(() => {
			return (
				ClinicalEvaluationCardSkeleton.defaultHeight +
				(parseFloat(styles.rowMargin) || cardMargin)
			)
		})
	} else {
		itemsHeights = []
		collectionItems = []
		data.forEach((item) => {
			const [height, textHeight] = calculateItemHeight(
				item,
				textsHeights,
				textSpecs.lineHeight,
				cardMargin,
				cards,
				detailLevel,
				screenSize
			)
			itemsHeights.push(height)
			collectionItems.push({
				...item,
				height,
				textHeight,
				$cardLabels: cardLabels,
			})
		})
	}

	return (
		<>
			{!isLoading && !collectionItems.length ? (
				<NoData
					applyMaxWidth
					paddings={['normal', 'normal']}
					message={i18n`This process has no **medical opinion**.`}
				/>
			) : null}
			<Collection
				className={styles['list']}
				ref={collectionRef}
				items={collectionItems}
				renderAhead={isBeforeTextComputation ? 0 : 10}
				itemsHeights={itemsHeights}
				cards={cards}
			/>
		</>
	)
}

export default ClinicalOpinionsController
